/**
 * Utility centralizzata per la formattazione uniforme delle date
 * Formato standard: gg/mm/aaaa per le date, gg/mm/aaaa hh:mm per date con ora
 * 
 * Design Pattern: Strategy Pattern per gestire diverse strategie di formattazione
 */

/**
 * Strategy per formattare solo la data
 * @param {Date} date - Oggetto Date da formattare
 * @returns {string} Data formattata come gg/mm/aaaa
 */
const formatDateOnly = (date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

/**
 * Strategy per formattare data e ora
 * @param {Date} date - Oggetto Date da formattare
 * @returns {string} Data e ora formattate come gg/mm/aaaa hh:mm
 */
const formatDateTimeStrategy = (date) => {
    const dateStr = formatDateOnly(date);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${dateStr} ${hours}:${minutes}`;
};

/**
 * Strategy per formattare data, ora e secondi
 * @param {Date} date - Oggetto Date da formattare
 * @returns {string} Data e ora formattate come gg/mm/aaaa hh:mm:ss
 */
const formatDateTimeSecondsStrategy = (date) => {
    const dateTimeStr = formatDateTimeStrategy(date);
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${dateTimeStr}:${seconds}`;
};

/**
 * Converte vari input in oggetto Date
 * @param {string|Date|number} input - Input da convertire
 * @returns {Date|null} Oggetto Date o null se input non valido
 */
const parseToDate = (input) => {
    if (!input) return null;
    
    if (input instanceof Date) {
        return isNaN(input.getTime()) ? null : input;
    }
    
    if (typeof input === 'string' || typeof input === 'number') {
        const date = new Date(input);
        return isNaN(date.getTime()) ? null : date;
    }
    
    return null;
};

/**
 * Formatta una data nel formato standard gg/mm/aaaa
 * 
 * @param {string|Date|number} input - Data da formattare (stringa ISO, oggetto Date, o timestamp)
 * @returns {string} Data formattata come gg/mm/aaaa o stringa vuota se input non valido
 * 
 * @example
 * formatDate('2026-02-17') // '17/02/2026'
 * formatDate(new Date(2026, 1, 17)) // '17/02/2026'
 * formatDate(1708185600000) // '17/02/2026'
 */
export const formatDate = (input) => {
    const date = parseToDate(input);
    return date ? formatDateOnly(date) : '';
};

/**
 * Formatta una data con ora nel formato standard gg/mm/aaaa hh:mm
 * 
 * @param {string|Date|number} input - Data da formattare (stringa ISO, oggetto Date, o timestamp)
 * @returns {string} Data e ora formattate come gg/mm/aaaa hh:mm o stringa vuota se input non valido
 * 
 * @example
 * formatDateTime('2026-02-17T14:30:00') // '17/02/2026 14:30'
 * formatDateTime(new Date(2026, 1, 17, 14, 30)) // '17/02/2026 14:30'
 */
export const formatDateTime = (input) => {
    const date = parseToDate(input);
    return date ? formatDateTimeStrategy(date) : '';
};

/**
 * Formatta una data con ora e secondi nel formato standard gg/mm/aaaa hh:mm:ss
 * 
 * @param {string|Date|number} input - Data da formattare
 * @returns {string} Data, ora e secondi formattati come gg/mm/aaaa hh:mm:ss
 * 
 * @example
 * formatDateTimeSeconds('2026-02-17T14:30:45') // '17/02/2026 14:30:45'
 */
export const formatDateTimeSeconds = (input) => {
    const date = parseToDate(input);
    return date ? formatDateTimeSecondsStrategy(date) : '';
};

/**
 * Formatta solo l'ora nel formato hh:mm
 * 
 * @param {string|Date|number} input - Data da cui estrarre l'ora
 * @returns {string} Ora formattata come hh:mm o stringa vuota se input non valido
 * 
 * @example
 * formatTime('2026-02-17T14:30:00') // '14:30'
 * formatTime(new Date(2026, 1, 17, 14, 30)) // '14:30'
 */
export const formatTime = (input) => {
    const date = parseToDate(input);
    if (!date) return '';
    
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
};

/**
 * Formatta l'ora attuale nel formato gg/mm/aaaa hh:mm
 * 
 * @returns {string} Data e ora correnti formattate
 * 
 * @example
 * formatNow() // '17/02/2026 14:30'
 */
export const formatNow = () => {
    return formatDateTime(new Date());
};

/**
 * Formatta l'ora attuale solo come data gg/mm/aaaa
 * 
 * @returns {string} Data corrente formattata
 * 
 * @example
 * formatToday() // '17/02/2026'
 */
export const formatToday = () => {
    return formatDate(new Date());
};

/**
 * Verifica se una data è valida
 * 
 * @param {string|Date|number} input - Data da verificare
 * @returns {boolean} true se la data è valida, false altrimenti
 */
export const isValidDate = (input) => {
    return parseToDate(input) !== null;
};

/**
 * Context per applicare diverse strategie di formattazione
 * Implementa il pattern Strategy
 */
class DateFormatterContext {
    constructor() {
        this.strategies = {
            date: formatDateOnly,
            datetime: formatDateTimeStrategy,
            datetimeseconds: formatDateTimeSecondsStrategy
        };
    }
    
    /**
     * Applica una strategia di formattazione
     * @param {Date} date - Data da formattare
     * @param {string} strategyName - Nome della strategia ('date', 'datetime', 'datetimeseconds')
     * @returns {string} Data formattata secondo la strategia
     */
    format(date, strategyName = 'date') {
        const strategy = this.strategies[strategyName];
        if (!strategy) {
            console.warn(`Strategia '${strategyName}' non trovata. Uso 'date' come default.`);
            return this.strategies.date(date);
        }
        return strategy(date);
    }
    
    /**
     * Registra una nuova strategia di formattazione
     * @param {string} name - Nome della strategia
     * @param {Function} strategy - Funzione di formattazione
     */
    registerStrategy(name, strategy) {
        this.strategies[name] = strategy;
    }
}

// Esporta un'istanza singleton del context
export const dateFormatterContext = new DateFormatterContext();

/**
 * Utility per convertire una data in formato ISO per le API
 * 
 * @param {string|Date|number} input - Data da convertire
 * @returns {string} Data in formato ISO (YYYY-MM-DD) o stringa vuota
 */
export const toISODate = (input) => {
    const date = parseToDate(input);
    if (!date) return '';
    
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Genera un range di date con mapping tra formato display e ISO
 * Utile per grafici dove le categories sono formattate ma i dati usano chiavi ISO
 * 
 * @param {string|Date} startDate - Data iniziale
 * @param {string|Date} endDate - Data finale
 * @returns {Object} { dates: string[], datesISO: string[] }
 * 
 * @example
 * const { dates, datesISO } = getDateRangeWithISO('2026-02-15', '2026-02-17');
 * // dates: ['15/02/2026', '16/02/2026', '17/02/2026']
 * // datesISO: ['2026-02-15', '2026-02-16', '2026-02-17']
 */
export const getDateRangeWithISO = (startDate, endDate) => {
    const dates = [];
    const datesISO = [];
    
    const start = parseToDate(startDate);
    const end = parseToDate(endDate);
    
    if (!start || !end) return { dates: [], datesISO: [] };
    
    let currentDate = new Date(start);
    while (currentDate <= end) {
        datesISO.push(toISODate(currentDate));
        dates.push(formatDate(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return { dates, datesISO };
};

/**
 * Genera un range di ore con mapping tra formato display e ISO
 * Utile per grafici orari dove le categories sono formattate ma i dati usano chiavi ISO
 * 
 * @param {string|Date} startDateTime - Data/ora iniziale (YYYY-MM-DD HH:mm:ss o Date)
 * @param {string|Date} endDateTime - Data/ora finale
 * @returns {Object} { dateTimes: string[], dateTimesISO: string[] }
 * 
 * @example
 * const { dateTimes, dateTimesISO } = getHourRangeWithISO('2026-02-17 10:00:00', '2026-02-17 14:00:00');
 * // dateTimes: ['17/02/2026 10:00', '17/02/2026 11:00', ...]
 * // dateTimesISO: ['2026-02-17 10', '2026-02-17 11', ...]
 */
export const getHourRangeWithISO = (startDateTime, endDateTime) => {
    const dateTimes = [];
    const dateTimesISO = [];
    
    // Parse le stringhe date/time
    let start = parseToDate(startDateTime);
    let end = parseToDate(endDateTime);
    
    if (!start || !end) return { dateTimes: [], dateTimesISO: [] };
    
    // Arrotonda all'ora
    start.setMinutes(0, 0, 0);
    
    let currentHour = new Date(start);
    while (currentHour <= end) {
        // Formato ISO per chiavi: "YYYY-MM-DD HH"
        const year = currentHour.getFullYear();
        const month = String(currentHour.getMonth() + 1).padStart(2, '0');
        const day = String(currentHour.getDate()).padStart(2, '0');
        const hour = String(currentHour.getHours()).padStart(2, '0');
        const hourISO = `${year}-${month}-${day} ${hour}`;
        
        dateTimesISO.push(hourISO);
        dateTimes.push(formatDateTime(currentHour));
        
        currentHour.setHours(currentHour.getHours() + 1);
    }
    
    return { dateTimes, dateTimesISO };
};

/**
 * Utility per convertire una data in formato ISO completo per le API
 * 
 * @param {string|Date|number} input - Data da convertire
 * @returns {string} Data in formato ISO (YYYY-MM-DD HH:mm:ss) o stringa vuota
 */
export const toISODateTime = (input) => {
    const date = parseToDate(input);
    if (!date) return '';
    
    const dateStr = toISODate(date);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${dateStr} ${hours}:${minutes}:${seconds}`;
};

// Export default per compatibilità
export default {
    formatDate,
    formatDateTime,
    formatDateTimeSeconds,
    formatTime,
    formatNow,
    formatToday,
    isValidDate,
    toISODate,
    toISODateTime,
    getDateRangeWithISO,
    getHourRangeWithISO,
    dateFormatterContext
};
